import React, { useCallback, useMemo, useState } from 'react';
import { Button, Loader, Tabs, Pagination, Select } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconDotsVertical, IconPencil, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { notifications } from '@mantine/notifications';
import { DataTable, DataTableColumn, DataTableProps } from 'mantine-datatable';

import { StandIn } from '../../codegen/graphql';
import { ConfirmDeleteModal, TableActions, TableWrapper } from '../common';
import { GET_STAND_INS } from '../../apollo/queries/standInManagement';
import StandInFormModal from './StandInFormModal';
import { DELETE_STAND_IN } from '../../apollo/mutations/standInManagement';
import { useDataTableSorting } from '../../hooks/useDataTableSorting';
import { commonTableStyles } from '../../constants/tableStyles';

function StandInTable() {
    const [addStandInModalOpened, standInModalHandlers] = useDisclosure(false);
    const [confirmDeleteModalOpened, confirmDeleteModalHandlers] =
        useDisclosure(false);
    const [activeTab, setActiveTab] = useState<string>('Active');
    const [selectedStandIn, setSelectedStandIn] = useState<StandIn | undefined>(
        undefined,
    );

    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 20,
    });

    const { sortStatus, handleSortStatusChange } =
        useDataTableSorting<StandIn>();

    const { data: standInsResponse, loading: fetchingStandIns } = useQuery(
        GET_STAND_INS,
        {
            variables: { historical: activeTab !== 'Active' },
        },
    );

    const [deleteStandIn, { loading: deleting }] = useMutation(
        DELETE_STAND_IN,
        {
            refetchQueries: [GET_STAND_INS],
        },
    );

    const deleteStandInHandler = useCallback(
        async (standIn: StandIn) => {
            try {
                await deleteStandIn({
                    variables: { id: standIn.id },
                });
                confirmDeleteModalHandlers.close();
                setSelectedStandIn(undefined);
            } catch (error) {
                console.error(error);
                notifications.show({
                    title: 'Error',
                    message: 'Failed to delete stand-in',
                    color: 'red',
                });
            }
        },
        [deleteStandIn, confirmDeleteModalHandlers],
    );

    const editStandIn = useCallback(
        (standIn: StandIn) => {
            setSelectedStandIn(standIn);
            standInModalHandlers.open();
        },
        [standInModalHandlers],
    );

    const options = useCallback(
        (standIn: StandIn) => [
            {
                label: 'Edit',
                icon: <IconPencil className="text-primary" size={20} />,
                onClick: () => editStandIn(standIn),
                isShown: true,
            },
            {
                label: 'Delete',
                icon: <IconTrash color="red" size={20} />,
                onClick: () => {
                    setSelectedStandIn(standIn);
                    confirmDeleteModalHandlers.open();
                },
                isShown: true,
            },
        ],
        [editStandIn, confirmDeleteModalHandlers],
    );

    const renderActions = useCallback(
        (standIn: StandIn) => {
            if (deleting && selectedStandIn?.id === standIn.id) {
                return <Loader size="sm" />;
            }

            return (
                <TableActions
                    key={standIn.id}
                    options={activeTab === 'Inactive' ? [] : options(standIn)}
                    row={standIn}
                >
                    <Button
                        variant="subtle"
                        className="text-primary rounded-md p-1 w-min h-min"
                    >
                        <IconDotsVertical />
                    </Button>
                </TableActions>
            );
        },
        [options, activeTab, deleting, selectedStandIn],
    );

    const renderFullName = useCallback(
        (standIn: StandIn) =>
            `${standIn.substituteUser.firstName} ${standIn.substituteUser.lastName}`,
        [],
    );

    const renderStartDate = useCallback(
        (standIn: StandIn) => dayjs(standIn.start).format('D MMM YYYY - HH:mm'),
        [],
    );

    const renderEndDate = useCallback(
        (standIn: StandIn) => dayjs(standIn.end).format('D MMM YYYY - HH:mm'),
        [],
    );

    const renderDurationInDays = useCallback(
        (standIn: StandIn) =>
            dayjs(standIn.end).diff(dayjs(standIn.start), 'days'),
        [],
    );

    const data = useMemo(
        () => standInsResponse?.getMyStandIns ?? [],
        [standInsResponse],
    );

    const columns = useMemo(
        (): DataTableColumn<StandIn>[] => [
            {
                accessor: 'standInName',
                title: 'Stand-In Name',
                width: 200,
                textAlign: 'left',
                render: renderFullName,
                sortable: true,
            },
            {
                accessor: 'start',
                title: 'Start Date & Time',
                width: 180,
                textAlign: 'left',
                render: renderStartDate,
                sortable: true,
            },
            {
                accessor: 'end',
                title: 'End Date & Time',
                width: 180,
                textAlign: 'left',
                render: renderEndDate,
                sortable: true,
            },
            {
                accessor: 'duration',
                title: 'Duration (Days)',
                width: 140,
                textAlign: 'center',
                render: renderDurationInDays,
                sortable: true,
            },
            {
                accessor: 'actions',
                title: 'Actions',
                width: 100,
                textAlign: 'center',
                render: renderActions,
                sortable: false,
            },
        ],
        [
            renderActions,
            renderStartDate,
            renderEndDate,
            renderFullName,
            renderDurationInDays,
        ],
    );

    const paginatedData = useMemo(() => {
        const startIndex = pagination.pageIndex * pagination.pageSize;
        const endIndex = startIndex + pagination.pageSize;
        return data.slice(startIndex, endIndex);
    }, [data, pagination]);

    const totalRecords = data.length;

    React.useEffect(() => {
        setPagination(prev => ({ ...prev, pageIndex: 0 }));
    }, [data.length]);

    return (
        <Tabs
            defaultValue="All"
            className=""
            onChange={value => setActiveTab(value ?? 'Active')}
            value={activeTab}
        >
            <Tabs.List>
                <Tabs.Tab value="Active">Active & Upcoming</Tabs.Tab>
                <Tabs.Tab value="Inactive">History</Tabs.Tab>
            </Tabs.List>

            <TableWrapper
                borderRadius="rounded-xl rounded-t-none"
                headerPadding="py-3"
                tableHeightOffset="15rem"
                pagination={
                    <div className="flex items-center gap-4 w-full justify-end">
                        <Select
                            data={['10', '20', '50', '100']}
                            value={pagination.pageSize.toString()}
                            onChange={value => {
                                if (value)
                                    setPagination({
                                        pageIndex: 0,
                                        pageSize: parseInt(value, 10),
                                    });
                            }}
                            size="xs"
                            style={{ width: 80 }}
                        />
                        <Pagination
                            value={pagination.pageIndex + 1}
                            total={Math.ceil(
                                totalRecords / pagination.pageSize,
                            )}
                            onChange={p =>
                                setPagination(prev => ({
                                    ...prev,
                                    pageIndex: p - 1,
                                }))
                            }
                            size="sm"
                        />
                        <span className="text-xs text-gray-500 ml-2">
                            {pagination.pageSize * pagination.pageIndex + 1}-
                            {Math.min(
                                pagination.pageSize *
                                    (pagination.pageIndex + 1),
                                totalRecords,
                            )}{' '}
                            of {totalRecords}
                        </span>
                    </div>
                }
            >
                <DataTable
                    {...({
                        withTableBorder: true,
                        striped: true,
                        verticalSpacing: 'xs',
                        fz: 'sm',
                        noRecordsText: 'No records to display',
                        withColumnBorders: false,
                        highlightOnHover: true,
                        records: paginatedData,
                        columns,
                        fetching: fetchingStandIns,
                        idAccessor: 'id',
                        onSortStatusChange: handleSortStatusChange,
                        classNames: {
                            table: 'selection-column-fix',
                        },
                        styles: commonTableStyles,
                        ...(sortStatus && { sortStatus }),
                    } as DataTableProps<StandIn>)}
                />
            </TableWrapper>
            <StandInFormModal
                onClose={standInModalHandlers.close}
                opened={addStandInModalOpened}
                standIn={selectedStandIn}
            />
            <ConfirmDeleteModal
                opened={confirmDeleteModalOpened}
                close={confirmDeleteModalHandlers.close}
                onConfirm={() =>
                    deleteStandInHandler(selectedStandIn as StandIn)
                }
                subject="Stand-In"
                message="Are you sure you want to delete this stand-in?"
                loading={deleting}
            />
        </Tabs>
    );
}

export default StandInTable;
